import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ComplaintList from "@/pages/ComplaintList";
import ComplaintNew from "@/pages/ComplaintNew";
import ComplaintDetail from "@/pages/ComplaintDetail";
import TechnicianAssignment from "@/pages/TechnicianAssignment";
import PartsRequests from "@/pages/PartsRequests";
import Inventory from "@/pages/Inventory";
import Technicians from "@/pages/Technicians";
import ServicePriceList from "@/pages/ServicePriceList";
import HappyCalls from "@/pages/HappyCalls";
import Reports from "@/pages/Reports";
import MyApprovals from "@/pages/MyApprovals";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import ImportComplaints from "@/pages/ImportComplaints";
import MapView from "@/pages/MapView";
import MyJobs from "@/pages/MyJobs";
import Tracker from "@/pages/Tracker";
import Roles from "@/pages/Roles";
import Users from "@/pages/Users";
import WarrantyMaster from "@/pages/WarrantyMaster";
import TechDashboard from "@/pages/TechDashboard";
import TechReports from "@/pages/TechReports";
import PartsLedger from "@/pages/PartsLedger";
import SparesDashboard from "@/pages/SparesDashboard";
import SparesStockEntry from "@/pages/SparesStockEntry";
import SparesPartTakeout from "@/pages/SparesPartTakeout";
import SparesPODList from "@/pages/SparesPODList";
import PartApprovalReport from "@/pages/PartApprovalReport";
import SparesPartsEntry from "@/pages/SparesPartsEntry";
import SparesMRNList from "@/pages/SparesMRNList";
import PartsTakenOutReport from "@/pages/PartsTakenOutReport";
import DealerPerformanceReport from "@/pages/DealerPerformanceReport";
import DealersReadOnly from "@/pages/DealersReadOnly";
import ProductsReadOnly from "@/pages/ProductsReadOnly";
import ProductRequest from "@/pages/ProductRequest";
import ProductApproval from "@/pages/ProductApproval";
import SentSpareDetails from "@/pages/SentSpareDetails";

// --- Promoter Portal (Phase 1) ---
import PromoterLayout from "@/components/PromoterLayout";
import PromoterDashboard from "@/pages/promoter/PromoterDashboard";
import PromoterManagement from "@/pages/promoter/PromoterManagement";
import PromoterOnboarding from "@/pages/promoter/PromoterOnboarding";
import PromoterEdit from "@/pages/promoter/PromoterEdit";
import PromoterComingSoon from "@/pages/promoter/PromoterComingSoon";
// --- Promoter Portal (Phase 2) ---
import PromoterAttendance from "@/pages/promoter/PromoterAttendance";
import PromoterMyAttendance from "@/pages/promoter/PromoterMyAttendance";
import PromoterLeaveRequest from "@/pages/promoter/PromoterLeaveRequest";
import PromoterSaleEntry from "@/pages/promoter/PromoterSaleEntry";
import PromoterMySales from "@/pages/promoter/PromoterMySales";
import PromoterSaleEdit from "@/pages/promoter/PromoterSaleEdit";
import AttendanceManagement from "@/pages/promoter/AttendanceManagement";
import SalesManagement from "@/pages/promoter/SalesManagement";
// --- Promoter Portal (Phase 3) ---
import SalesApproval from "@/pages/promoter/SalesApproval";
import FinancialRules from "@/pages/promoter/FinancialRules";
import AdminFinancialApprovals from "@/pages/promoter/AdminFinancialApprovals";
import ApprovalCenter from "@/pages/promoter/ApprovalCenter";
// --- Promoter Portal (Phase 4) ---
import IncentiveSummary from "@/pages/promoter/IncentiveSummary";
import PromoterMyEarnings from "@/pages/promoter/PromoterMyEarnings";
// --- Promoter Portal (Phase 5) ---
import PromoterStockReport from "@/pages/promoter/PromoterStockReport";
import PromoterMyStockReports from "@/pages/promoter/PromoterMyStockReports";
import StockReportsManagement from "@/pages/promoter/StockReportsManagement";
import TerminationManagement from "@/pages/promoter/TerminationManagement";
import SellOutSchemes from "@/pages/promoter/SellOutSchemes";
import PromoterEligibleSchemes from "@/pages/promoter/PromoterEligibleSchemes";
import BulkIncentiveSetup from "@/pages/promoter/BulkIncentiveSetup";
// --- Promoter Portal (Phase 6) ---
import PromoterMyReports from "@/pages/promoter/PromoterMyReports";

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <TechRouteGuard>
        <SparesRouteGuard>
          <SalesRouteGuard>
            <PromoterRouteGuard>
              <Layout>{children}</Layout>
            </PromoterRouteGuard>
          </SalesRouteGuard>
        </SparesRouteGuard>
      </TechRouteGuard>
    </ProtectedRoute>
  );
}

// PromoterShell wraps ALL /promoter/* routes. PromoterLayout chooses the right
// chrome inside (mobile bottom-nav for sales_promoter, desktop side-nav for
// admin/PM/sales_head/sales_manager). Sales promoters are also blocked from
// the desktop management paths (manage, sales-approval, financial-*, etc.).
function PromoterShell({ children }) {
  return (
    <ProtectedRoute>
      <PromoterPortalGuard>
        <PromoterLayout>{children}</PromoterLayout>
      </PromoterPortalGuard>
    </ProtectedRoute>
  );
}

// Block sales_promoter from desktop-only promoter paths. Admin/PM/SH/SM see everything.
const PROMOTER_DESKTOP_ONLY = [
  "/promoter/manage",
  "/promoter/attendance-mgmt",
  "/promoter/sales-mgmt",
  "/promoter/sales-approval",
  "/promoter/financial-rules",
  "/promoter/financial-approvals",
  "/promoter/incentive-summary",
  "/promoter/stock-mgmt",
  "/promoter/terminations",
];

function PromoterPortalGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.role === "sales_promoter") {
    const path = location.pathname;
    const blocked = PROMOTER_DESKTOP_ONLY.some((p) => path === p || path.startsWith(p + "/"));
    if (blocked) return <Navigate to="/promoter" replace />;
  }
  return children;
}

const TECH_ALLOWED = ["/my-jobs", "/technician/dashboard", "/technician/reports", "/technician/parts-ledger", "/notifications"];

function TechRouteGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  if (user?.role === "technician") {
    // Technicians can ONLY visit allowed mobile pages + complaint DETAIL (uuid-shaped).
    // Explicitly disallow /complaints/new and the company /complaints list.
    const isComplaintDetail = /^\/complaints\/[0-9a-f-]{8,}$/i.test(path);
    const allowed = isComplaintDetail || TECH_ALLOWED.some((p) => path === p || path.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/technician/dashboard" replace />;
  }
  return children;
}

// Allowed paths for Spares Assistant — strictly spares-related screens only.
// No company dashboard, no New Complaint, no admin/users/roles/warranty/settings/etc.
const SPARES_ALLOWED = [
  "/spares/dashboard",
  "/spares/parts-entry",
  "/spares/mrn",
  "/spares/stock-entry",   // legacy redirect target
  "/spares/part-takeout",
  "/spares/pods",
  "/spares/part-approval-report",
  "/spares/parts-taken-out-report",
  "/sent-spare-details",
  "/parts-requests",
  "/inventory",            // admin/manager view; redirect for spares handled below
  "/complaints",
  "/notifications",
  "/reports",
];

function SparesRouteGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  if (user?.role === "spares") {
    // Block /complaints/new explicitly even though /complaints is allowed for read.
    if (path === "/complaints/new") return <Navigate to="/spares/dashboard" replace />;
    // Spares should land on the unified parts entry — legacy /inventory + /spares/stock-entry redirect.
    if (path === "/inventory" || path === "/spares/stock-entry")
      return <Navigate to="/spares/parts-entry" replace />;
    const isComplaintDetail = /^\/complaints\/[0-9a-f-]{8,}$/i.test(path);
    const allowed = isComplaintDetail
      || SPARES_ALLOWED.some((p) => path === p || path.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/spares/dashboard" replace />;
  }
  return children;
}

// Allowed paths for Sales Team — read-only across dealer reporting, complaints,
// dealers, products, dashboard. NO admin/users/roles/import/spares/etc.
const SALES_ALLOWED = [
  "/dashboard",
  "/reports/dealer-performance",
  "/reports",
  "/complaints",
  "/dealers",
  "/products",
  "/notifications",
];

function SalesRouteGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  if (user?.role === "sales") {
    // Block any write paths sales should never reach
    if (path === "/complaints/new") return <Navigate to="/reports/dealer-performance" replace />;
    if (path.startsWith("/admin")) return <Navigate to="/reports/dealer-performance" replace />;
    if (path === "/settings") return <Navigate to="/reports/dealer-performance" replace />;
    if (path === "/inventory" || path.startsWith("/spares") || path === "/my-jobs"
        || path.startsWith("/technician") || path === "/parts-requests"
        || path === "/assignment" || path === "/import" || path === "/happy-calls"
        || path === "/service-prices" || path === "/map" || path === "/technicians") {
      return <Navigate to="/reports/dealer-performance" replace />;
    }
    const isComplaintDetail = /^\/complaints\/[0-9a-f-]{8,}$/i.test(path);
    const allowed = isComplaintDetail
      || SALES_ALLOWED.some((p) => path === p || path.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/reports/dealer-performance" replace />;
  }
  return children;
}

// Sales Promoter — strictly /promoter/* (their own mobile portal). Block all CRM paths.
function PromoterRouteGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  if (user?.role === "sales_promoter") {
    if (!path.startsWith("/promoter")) return <Navigate to="/promoter" replace />;
    // The mobile portal also has guards against accidentally landing on /promoter/manage/* (PM-only).
    if (path.startsWith("/promoter/manage")) return <Navigate to="/promoter" replace />;
  }
  return children;
}

function DashboardGate() {
  // Each role gets its OWN scoped dashboard. Spares + Tech never see the company dashboard.
  // Sales sees the full read-only Operations Dashboard (write buttons hidden in Dashboard.jsx).
  const { user } = useAuth();
  if (user?.role === "technician") return <TechDashboard />;
  if (user?.role === "spares") return <SparesDashboard />;
  return <Dashboard />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "technician") return <Navigate to="/technician/dashboard" replace />;
  if (user.role === "spares") return <Navigate to="/spares/dashboard" replace />;
  if (user.role === "sales") return <Navigate to="/reports/dealer-performance" replace />;
  if (user.role === "sales_promoter") return <Navigate to="/promoter" replace />;
  // product_manager / sales_head / sales_manager / accounts_finance land on the
  // CRM dashboard for now; they reach the promoter module via sidebar entries.
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/track" element={<Tracker />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={<Shell><DashboardGate /></Shell>} />
            <Route path="/my-jobs" element={<Shell><MyJobs /></Shell>} />
            <Route path="/technician/dashboard" element={<Shell><TechDashboard /></Shell>} />
            <Route path="/technician/reports" element={<Shell><TechReports /></Shell>} />
            <Route path="/technician/parts-ledger" element={<Shell><PartsLedger /></Shell>} />
            <Route path="/spares/dashboard" element={<Shell><SparesDashboard /></Shell>} />
            <Route path="/spares/stock-entry" element={<Shell><SparesStockEntry /></Shell>} />
            <Route path="/spares/part-takeout" element={<Shell><SparesPartTakeout /></Shell>} />
            <Route path="/spares/pods" element={<Shell><SparesPODList /></Shell>} />
            <Route path="/spares/part-approval-report" element={<Shell><PartApprovalReport /></Shell>} />
            <Route path="/spares/parts-entry" element={<Shell><SparesPartsEntry /></Shell>} />
            <Route path="/spares/mrn" element={<Shell><SparesMRNList /></Shell>} />
            <Route path="/spares/parts-taken-out-report" element={<Shell><PartsTakenOutReport /></Shell>} />
            <Route path="/reports/dealer-performance" element={<Shell><DealerPerformanceReport /></Shell>} />
            <Route path="/my-approvals" element={<Shell><MyApprovals /></Shell>} />
            <Route path="/dealers" element={<Shell><DealersReadOnly /></Shell>} />
            <Route path="/products" element={<Shell><ProductsReadOnly /></Shell>} />
            <Route path="/product-requests" element={<Shell><ProductRequest /></Shell>} />
            <Route path="/product-approval" element={<Shell><ProductApproval /></Shell>} />
            <Route path="/sent-spare-details" element={<Shell><SentSpareDetails /></Shell>} />
            <Route path="/complaints" element={<Shell><ComplaintList /></Shell>} />
            <Route path="/complaints/new" element={<Shell><ComplaintNew /></Shell>} />
            <Route path="/complaints/:id" element={<Shell><ComplaintDetail /></Shell>} />
            <Route path="/assignment" element={<Shell><TechnicianAssignment /></Shell>} />
            <Route path="/parts-requests" element={<Shell><PartsRequests /></Shell>} />
            <Route path="/inventory" element={<Shell><Inventory /></Shell>} />
            <Route path="/technicians" element={<Shell><Technicians /></Shell>} />
            <Route path="/map" element={<Shell><MapView /></Shell>} />
            <Route path="/service-prices" element={<Shell><ServicePriceList /></Shell>} />
            <Route path="/happy-calls" element={<Shell><HappyCalls /></Shell>} />
            <Route path="/notifications" element={<Shell><Notifications /></Shell>} />
            <Route path="/import" element={<Shell><ImportComplaints /></Shell>} />
            <Route path="/reports" element={<Shell><Reports /></Shell>} />
            <Route path="/admin/roles" element={<Shell><Roles /></Shell>} />
            <Route path="/admin/users" element={<Shell><Users /></Shell>} />
            <Route path="/admin/warranty" element={<Shell><WarrantyMaster /></Shell>} />
            <Route path="/settings" element={<Shell><Settings /></Shell>} />

            {/* --- Promoter Portal --- */}
            {/* All /promoter/* routes live inside PromoterShell (the dedicated Promoter Portal layout). */}
            {/* The Service CRM Layout never renders for these routes. */}
            <Route path="/promoter" element={<PromoterShell><PromoterDashboard /></PromoterShell>} />
            <Route path="/promoter/attendance" element={<PromoterShell><PromoterAttendance /></PromoterShell>} />
            <Route path="/promoter/my-attendance" element={<PromoterShell><PromoterMyAttendance /></PromoterShell>} />
            <Route path="/promoter/leave" element={<PromoterShell><PromoterLeaveRequest /></PromoterShell>} />
            <Route path="/promoter/sales" element={<PromoterShell><PromoterMySales /></PromoterShell>} />
            <Route path="/promoter/sales/new" element={<PromoterShell><PromoterSaleEntry /></PromoterShell>} />
            <Route path="/promoter/sales/:id/edit" element={<PromoterShell><PromoterSaleEdit /></PromoterShell>} />
            <Route path="/promoter/my-sales" element={<PromoterShell><PromoterMySales /></PromoterShell>} />
            <Route path="/promoter/my-incentive" element={<PromoterShell><PromoterMyEarnings /></PromoterShell>} />
            <Route path="/promoter/stock" element={<PromoterShell><PromoterMyStockReports /></PromoterShell>} />
            <Route path="/promoter/stock/new" element={<PromoterShell><PromoterStockReport /></PromoterShell>} />
            <Route path="/promoter/reports" element={<PromoterShell><PromoterMyReports /></PromoterShell>} />
            {/* Desktop management (PM / Admin / Sales Head / Sales Manager) — same portal layout, desktop side-nav. */}
            <Route path="/promoter/manage" element={<PromoterShell><PromoterManagement /></PromoterShell>} />
            <Route path="/promoter/manage/new" element={<PromoterShell><PromoterOnboarding /></PromoterShell>} />
            <Route path="/promoter/manage/:id" element={<PromoterShell><PromoterEdit /></PromoterShell>} />
            <Route path="/promoter/attendance-mgmt" element={<PromoterShell><AttendanceManagement /></PromoterShell>} />
            <Route path="/promoter/sales-mgmt" element={<PromoterShell><SalesManagement /></PromoterShell>} />
            <Route path="/promoter/sales-approval" element={<PromoterShell><SalesApproval /></PromoterShell>} />
            <Route path="/promoter/financial-rules" element={<PromoterShell><FinancialRules /></PromoterShell>} />
            <Route path="/promoter/financial-approvals" element={<PromoterShell><AdminFinancialApprovals /></PromoterShell>} />
            <Route path="/promoter/approval-center" element={<PromoterShell><ApprovalCenter /></PromoterShell>} />
            <Route path="/promoter/incentive-summary" element={<PromoterShell><IncentiveSummary /></PromoterShell>} />
            <Route path="/promoter/stock-mgmt" element={<PromoterShell><StockReportsManagement /></PromoterShell>} />
            <Route path="/promoter/terminations" element={<PromoterShell><TerminationManagement /></PromoterShell>} />
            <Route path="/promoter/schemes" element={<PromoterShell><SellOutSchemes /></PromoterShell>} />
            <Route path="/promoter/my-schemes" element={<PromoterShell><PromoterEligibleSchemes /></PromoterShell>} />
            <Route path="/promoter/bulk-incentive" element={<PromoterShell><BulkIncentiveSetup /></PromoterShell>} />

            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
